// بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
//
// Channels & connections wiring for the Бриф promotion console (SPEC §3).
// Covers: connection registry + health (soft reads), connect/disconnect
// (strict writes), Buffer org/channel discovery (20-min sessionStorage cache +
// 429 Retry-After handling), Telegram bot management, OpenAI/Codex device auth,
// model selection, cookie validation and the OAuth popup URL builder.
//
// Read endpoints used by the operational screens go through `softCall` (bearer,
// never logs out, mock fallback). Everything that writes — or that the user
// triggers explicitly and expects real errors from — goes through
// `publisherCall` (strict: 401 → global logout, throws on !ok).
//
// Paths are verbatim (NOT rewritten): connections / buffer / oauth / telegram /
// openai are all under `/api/*`. There is no response envelope. A handful of
// endpoints (telegram, validate-cookies, codex-device-status) return
// `{ ok: false, error }` at HTTP 200 — we branch on `j.ok`, not `res.ok`.

import { publisherCall, softCall, type Loaded } from './client';
import type {
  BufferChannel,
  BufferOrganization,
  ChannelReadiness,
  ConnectionPlatform,
  ConnectionSummary,
  Platform,
} from './types';

// ── Constants ────────────────────────────────────────────────────

/** Telegram webhook secret — hardcoded contract value shared with the worker. */
const TELEGRAM_WEBHOOK_SECRET = 'summio-bot-7m9k2x4n';

/** sessionStorage key for the cached Buffer org/channel snapshot. */
const BUFFER_CACHE_KEY = 'summio:promotion:buffer-state:v1';

/** Buffer cache TTL — 20 minutes. */
const BUFFER_CACHE_TTL_MS = 20 * 60 * 1000;

/** The 8 social platforms the readiness/health endpoint reports on. */
const PLATFORMS: Platform[] = [
  'telegram',
  'twitter',
  'instagram',
  'threads',
  'facebook',
  'linkedin',
  'youtube',
  'tiktok',
];

/** The 12 connection registry slots (8 social + reddit/producthunt + 2 AI). */
const CONNECTION_PLATFORMS: ConnectionPlatform[] = [
  'telegram',
  'twitter',
  'instagram',
  'threads',
  'facebook',
  'linkedin',
  'youtube',
  'tiktok',
  'reddit',
  'producthunt',
  'openai',
  'gemini',
];

// ── Small helpers ────────────────────────────────────────────────

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

// ── Connections (soft read) ──────────────────────────────────────

/** A single connection summary, normalised + the fallback "not configured" slot. */
function mapConnection(platform: ConnectionPlatform, raw: unknown): ConnectionSummary {
  const row = asRecord(raw);
  const status = row.status;
  const credentialKeys = Array.isArray(row.credential_keys)
    ? (row.credential_keys as unknown[]).filter((k): k is string => typeof k === 'string')
    : [];
  return {
    platform,
    status:
      status === 'connected' || status === 'error' || status === 'expired' || status === 'not_configured'
        ? status
        : 'not_configured',
    has_credentials: row.has_credentials === true,
    credential_keys: credentialKeys,
    metadata: asRecord(row.metadata),
    connected_at: asString(row.connected_at),
  };
}

/**
 * GET `/api/connections` → 12 ConnectionSummary slots (always present).
 * The worker may return `connections` as an array or as a platform-keyed object;
 * we normalise to the canonical 12-slot list. SOFT read.
 */
export function getConnections(): Promise<Loaded<ConnectionSummary[]>> {
  const fallback: ConnectionSummary[] = CONNECTION_PLATFORMS.map((p) => mapConnection(p, undefined));
  return softCall('/api/connections', fallback, (json) => {
    const root = asRecord(json);
    const conn = root.connections;
    const byPlatform = new Map<string, unknown>();
    if (Array.isArray(conn)) {
      for (const entry of conn) {
        const row = asRecord(entry);
        const p = asString(row.platform);
        if (p) byPlatform.set(p, entry);
      }
    } else {
      const obj = asRecord(conn);
      for (const key of Object.keys(obj)) byPlatform.set(key, obj[key]);
    }
    return CONNECTION_PLATFORMS.map((p) => mapConnection(p, byPlatform.get(p)));
  });
}

// ── Health / readiness (soft read) ───────────────────────────────

function asReadinessStatus(value: unknown): ChannelReadiness['status'] {
  return value === 'ready' || value === 'not_connected' || value === 'needs_attention' || value === 'unsupported'
    ? value
    : 'not_connected';
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

function mapBufferChannel(raw: unknown): BufferChannel | undefined {
  const row = asRecord(raw);
  const id = asString(row.id);
  if (!id) return undefined;
  return {
    id,
    name: asString(row.name),
    displayName: asString(row.displayName),
    service: asString(row.service) ?? '',
    avatar: asString(row.avatar),
    isQueuePaused: row.isQueuePaused === true,
  };
}

function mapReadiness(platform: Platform, raw: unknown): ChannelReadiness {
  const row = asRecord(raw);
  const bufferChannels = Array.isArray(row.buffer_channels)
    ? (row.buffer_channels as unknown[]).map(mapBufferChannel).filter((c): c is BufferChannel => !!c)
    : undefined;
  return {
    platform,
    ready: row.ready === true,
    status: asReadinessStatus(row.status),
    method: row.method === 'direct' || row.method === 'buffer' ? row.method : undefined,
    direct_ready: typeof row.direct_ready === 'boolean' ? row.direct_ready : undefined,
    buffer_ready: typeof row.buffer_ready === 'boolean' ? row.buffer_ready : undefined,
    account: asString(row.account),
    buffer_channel: mapBufferChannel(row.buffer_channel),
    buffer_channels: bufferChannels,
    reasons: asStringArray(row.reasons),
    warnings: asStringArray(row.warnings),
    requirements: asStringArray(row.requirements),
  };
}

/**
 * GET `/api/connections/health?post_id=` → ChannelReadiness for the 8 PLATFORMS.
 * SOFT read; degrades to "not connected" placeholders. Pass `postId` to scope
 * the readiness check to a specific post's drafts.
 */
export function getHealth(postId?: string): Promise<Loaded<ChannelReadiness[]>> {
  const path = postId
    ? `/api/connections/health?post_id=${encodeURIComponent(postId)}`
    : '/api/connections/health';
  const fallback: ChannelReadiness[] = PLATFORMS.map((p) => ({
    platform: p,
    ready: false,
    status: 'not_connected',
    reasons: [],
    warnings: [],
    requirements: [],
  }));
  return softCall(path, fallback, (json) => {
    const checks = asRecord(json).checks;
    const byPlatform = new Map<string, unknown>();
    if (Array.isArray(checks)) {
      for (const entry of checks) {
        const p = asString(asRecord(entry).platform);
        if (p) byPlatform.set(p, entry);
      }
    }
    return PLATFORMS.map((p) => mapReadiness(p, byPlatform.get(p)));
  });
}

// ── Connect / disconnect (strict write) ──────────────────────────

export interface ConnectPlatformPayload {
  credentials?: Record<string, string>;
  metadata?: Record<string, unknown>;
}

/**
 * POST `/api/connections/:platform` — saves credentials/metadata. Empty-string
 * credential values are dropped before sending (worker contract). STRICT write
 * → `{ connection: ConnectionSummary }`.
 */
export function connectPlatform(
  platform: ConnectionPlatform,
  payload: ConnectPlatformPayload,
): Promise<{ connection: ConnectionSummary }> {
  const body: ConnectPlatformPayload = {};
  if (payload.credentials) {
    const cleaned: Record<string, string> = {};
    for (const [key, value] of Object.entries(payload.credentials)) {
      if (typeof value === 'string' && value.length > 0) cleaned[key] = value;
    }
    body.credentials = cleaned;
  }
  if (payload.metadata) body.metadata = payload.metadata;

  return publisherCall<{ connection: ConnectionSummary }>(`/api/connections/${platform}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/** DELETE `/api/connections/:platform` → `{ ok: true }`. STRICT write. */
export function disconnectPlatform(platform: ConnectionPlatform): Promise<{ ok: boolean }> {
  return publisherCall<{ ok: boolean }>(`/api/connections/${platform}`, { method: 'DELETE' });
}

// ── Buffer (strict reads, 20-min cache, 429 handling) ────────────

interface BufferCacheEntry {
  fetched_at: number;
  organizations?: BufferOrganization[];
  channels?: Record<string, BufferChannel[]>; // keyed by organization_id
}

/** A 429 from Buffer carries retry hints; we surface them to the caller. */
export class BufferRateLimitError extends Error {
  readonly retryAfterSeconds?: number;
  readonly retryAt?: string;
  readonly bufferError?: string;

  constructor(message: string, opts: { retryAfterSeconds?: number; retryAt?: string; bufferError?: string }) {
    super(message);
    this.name = 'BufferRateLimitError';
    this.retryAfterSeconds = opts.retryAfterSeconds;
    this.retryAt = opts.retryAt;
    this.bufferError = opts.bufferError;
  }
}

function readBufferCache(): BufferCacheEntry | null {
  try {
    const raw = sessionStorage.getItem(BUFFER_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as BufferCacheEntry;
    if (typeof parsed.fetched_at !== 'number') return null;
    if (Date.now() - parsed.fetched_at > BUFFER_CACHE_TTL_MS) {
      sessionStorage.removeItem(BUFFER_CACHE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeBufferCache(mutate: (entry: BufferCacheEntry) => void): void {
  try {
    const existing = readBufferCache();
    const entry: BufferCacheEntry = existing ?? { fetched_at: Date.now() };
    // A write refreshes the timestamp so the whole snapshot ages together.
    entry.fetched_at = existing?.fetched_at ?? Date.now();
    mutate(entry);
    sessionStorage.setItem(BUFFER_CACHE_KEY, JSON.stringify(entry));
  } catch {
    /* sessionStorage may be unavailable (private mode); cache is best-effort */
  }
}

/**
 * Wraps a Buffer fetch with strict bearer auth and 429 Retry-After handling.
 * Buffer endpoints are reads but go through `publisherFetch` (strict) since a
 * 401 there means the operator's session is genuinely dead. We re-implement the
 * raw call here (rather than `publisherCall`) so we can read the Retry-After
 * header and the `{ retry_after_seconds, retry_at, buffer_error }` body on 429.
 */
async function bufferFetch<T>(path: string, map: (json: unknown) => T): Promise<T> {
  const { publisherFetch } = await import('@/services/publisherApi');
  const res = await publisherFetch(path);
  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    json = null;
  }

  if (res.status === 429) {
    const body = asRecord(json);
    const headerRetry = Number(res.headers.get('Retry-After'));
    const retryAfterSeconds =
      typeof body.retry_after_seconds === 'number'
        ? body.retry_after_seconds
        : Number.isFinite(headerRetry)
          ? headerRetry
          : undefined;
    throw new BufferRateLimitError(asString(body.error) ?? 'Buffer rate limit', {
      retryAfterSeconds,
      retryAt: asString(body.retry_at),
      bufferError: asString(body.buffer_error),
    });
  }

  if (!res.ok) {
    const message = asString(asRecord(json).error) ?? `HTTP ${res.status}`;
    throw new Error(message);
  }

  return map(json);
}

/**
 * GET `/api/buffer/organizations` → BufferOrganization[]. Served from the 20-min
 * sessionStorage cache when fresh; otherwise fetched and cached. 429 → throws
 * `BufferRateLimitError`.
 */
export async function bufferOrganizations(): Promise<BufferOrganization[]> {
  const cached = readBufferCache();
  if (cached?.organizations) return cached.organizations;

  const organizations = await bufferFetch('/api/buffer/organizations', (json) => {
    const list = asRecord(json).organizations;
    if (!Array.isArray(list)) return [];
    return list.map((entry): BufferOrganization => {
      const row = asRecord(entry);
      return {
        id: asString(row.id) ?? '',
        name: asString(row.name) ?? '',
        ownerEmail: asString(row.ownerEmail),
      };
    });
  });

  writeBufferCache((entry) => {
    entry.organizations = organizations;
  });
  return organizations;
}

/**
 * GET `/api/buffer/channels?organization_id=` → BufferChannel[]. Cached per
 * organization in the same 20-min sessionStorage snapshot. 429 → throws
 * `BufferRateLimitError`.
 */
export async function bufferChannels(orgId: string): Promise<BufferChannel[]> {
  const cached = readBufferCache();
  const hit = cached?.channels?.[orgId];
  if (hit) return hit;

  const channels = await bufferFetch(
    `/api/buffer/channels?organization_id=${encodeURIComponent(orgId)}`,
    (json) => {
      const list = asRecord(json).channels;
      if (!Array.isArray(list)) return [];
      return list.map((entry) => mapBufferChannel(entry)).filter((c): c is BufferChannel => !!c);
    },
  );

  writeBufferCache((entry) => {
    entry.channels = { ...(entry.channels ?? {}), [orgId]: channels };
  });
  return channels;
}

/** Manually drop the cached Buffer snapshot (e.g. after a (re)connect). */
export function clearBufferCache(): void {
  try {
    sessionStorage.removeItem(BUFFER_CACHE_KEY);
  } catch {
    /* best-effort */
  }
}

// ── Telegram (strict; branch on j.ok at HTTP 200) ────────────────

export interface TelegramBotInfo {
  id?: number;
  username?: string;
  first_name?: string;
  [key: string]: unknown;
}

export interface TelegramValidateTokenResult {
  ok: boolean;
  bot?: TelegramBotInfo;
  error?: string;
}

/** POST `/api/telegram/validate-token` `{ token }` → `{ ok, bot?, error? }`. */
export function telegramValidateToken(token: string): Promise<TelegramValidateTokenResult> {
  return publisherCall<TelegramValidateTokenResult>('/api/telegram/validate-token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
}

export interface TelegramChatInfo {
  id?: number | string;
  title?: string;
  username?: string;
  type?: string;
  [key: string]: unknown;
}

export interface TelegramGetChatResult {
  ok: boolean;
  chat?: TelegramChatInfo;
  error?: string;
}

/** POST `/api/telegram/get-chat` `{ token, chat_id }` → `{ ok, chat?, error? }`. */
export function telegramGetChat(token: string, chatId: string): Promise<TelegramGetChatResult> {
  return publisherCall<TelegramGetChatResult>('/api/telegram/get-chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, chat_id: chatId }),
  });
}

export interface TelegramSetupChannel {
  id: string;
  title?: string;
  username?: string;
}

export interface TelegramSetupBotResult {
  ok: boolean;
  webhook_registered?: boolean;
  webhook_error?: string;
  test_results?: unknown;
  error?: string;
}

/**
 * POST `/api/telegram/setup-bot` `{ token, channels, webhook_secret }`. The
 * webhook secret is the hardcoded contract value — callers never supply it.
 */
export function telegramSetupBot(
  token: string,
  channels: TelegramSetupChannel[],
): Promise<TelegramSetupBotResult> {
  return publisherCall<TelegramSetupBotResult>('/api/telegram/setup-bot', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, channels, webhook_secret: TELEGRAM_WEBHOOK_SECRET }),
  });
}

export interface TelegramChannelsResult {
  ok: boolean;
  channels?: unknown[];
  error?: string;
}

/** POST `/api/telegram/channels` `{ id }` → `{ ok, channels?, error? }`. */
export function telegramAddChannel(id: string): Promise<TelegramChannelsResult> {
  return publisherCall<TelegramChannelsResult>('/api/telegram/channels', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id }),
  });
}

/** DELETE `/api/telegram/channels?id=` → `{ ok, channels?, error? }`. */
export function telegramRemoveChannel(id: string): Promise<TelegramChannelsResult> {
  return publisherCall<TelegramChannelsResult>(
    `/api/telegram/channels?id=${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  );
}

export interface TelegramAllowedUsersResult {
  ok: boolean;
  allowed_user_ids: number[];
  error?: string;
}

/** POST `/api/telegram/allowed-users` `{ user_id }` → `{ ok, allowed_user_ids }`. */
export function telegramAllowedUsersAdd(userId: number): Promise<TelegramAllowedUsersResult> {
  return publisherCall<TelegramAllowedUsersResult>('/api/telegram/allowed-users', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId }),
  });
}

/** DELETE `/api/telegram/allowed-users?user_id=` → `{ ok, allowed_user_ids }`. */
export function telegramAllowedUsersRemove(userId: number): Promise<TelegramAllowedUsersResult> {
  return publisherCall<TelegramAllowedUsersResult>(
    `/api/telegram/allowed-users?user_id=${encodeURIComponent(String(userId))}`,
    { method: 'DELETE' },
  );
}

// ── OpenAI / Codex device auth (strict) ──────────────────────────

export interface CodexDeviceStartResult {
  session_id: string;
  activation_url: string;
  user_code: string | null;
  started_at: string;
}

/**
 * POST `/api/openai/codex-device-start` → device-auth session.
 * STRICT; surfaces 410/502 errors via the thrown `Error`.
 */
export function codexDeviceStart(): Promise<CodexDeviceStartResult> {
  return publisherCall<CodexDeviceStartResult>('/api/openai/codex-device-start', {
    method: 'POST',
  });
}

export interface CodexDeviceStatusResult {
  status: 'starting' | 'waiting' | 'complete' | 'error';
  account_id?: string;
  expires_at?: string;
  user_code?: string;
  activation_url?: string;
  message?: string;
  code?: string;
  error?: string;
}

/**
 * GET `/api/openai/codex-device-status?session_id=&started_at=&finalize=` —
 * ALWAYS HTTP 200; branch on `status` (and `code` for
 * `codex_device_session_expired`). Pass `finalize:true` when the user clicks the
 * finish button. Poll every 3s up to the 15-min cap (caller's responsibility).
 */
export function codexDeviceStatus(
  sessionId: string,
  startedAt: string,
  finalize?: boolean,
): Promise<CodexDeviceStatusResult> {
  const params = new URLSearchParams({ session_id: sessionId, started_at: startedAt });
  if (finalize) params.set('finalize', '1');
  return publisherCall<CodexDeviceStatusResult>(
    `/api/openai/codex-device-status?${params.toString()}`,
  );
}

// ── OpenAI models (strict) ───────────────────────────────────────

export interface SaveModelsPayload {
  text?: string;
  image?: string;
  reasoning?: string;
}

export interface SaveModelsResult {
  ok: boolean;
  models: { text?: string; image?: string; reasoning?: string };
}

/**
 * POST `/api/openai/models` `{ text?, image?, reasoning? }` → `{ ok, models }`.
 * STRICT; 404 `openai not connected` surfaces via the thrown `Error`.
 */
export function saveModels(payload: SaveModelsPayload): Promise<SaveModelsResult> {
  return publisherCall<SaveModelsResult>('/api/openai/models', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

// ── Cookie validation (strict; branch on j.ok at HTTP 200) ───────

export interface ValidateCookiesResult {
  ok: boolean;
  username?: string;
  name?: string;
  handle?: string;
  vanity?: string;
  id?: string;
  auth_mode?: string;
  error?: string;
  error_message?: string;
  error_code?: string;
}

/**
 * GET `/api/:platform/validate-cookies` → 200 `{ ok, ... }`. STRICT (the operator
 * triggers it and wants real failures), but the worker reports validation
 * failures via `ok:false` at HTTP 200 — branch on `j.ok`, not `res.ok`.
 */
export function validateCookies(platform: Platform): Promise<ValidateCookiesResult> {
  return publisherCall<ValidateCookiesResult>(`/api/${platform}/validate-cookies`);
}

// ── OAuth popup URL builder ──────────────────────────────────────

export type OAuthProvider = 'twitter' | 'instagram' | 'threads' | 'facebook' | 'linkedin';

/**
 * Builds the popup URL for the OAuth start endpoint:
 * `GET /api/oauth/:provider/start?return=<returnTo>`. The callback postMessages
 * `{ type:'summio-publisher-oauth', provider, connected, error }` back to the
 * opener (origin-checked by the caller). This is a pure string builder — no
 * fetch — because the flow runs in a popup window.
 */
export function oauthStartUrl(provider: OAuthProvider, returnTo: string): string {
  return `/api/oauth/${provider}/start?return=${encodeURIComponent(returnTo)}`;
}
